import React, { useEffect, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_ops_assistant_confirm_action } from "../../../constants";
import { toast } from "react-toastify";

const MAX_LEN = 5000;

export default function SupportRequestProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  onStatusChange,
}) {
  const summary = proposal.summary || {};
  const [status, setStatus] = useState(proposal.status || "pending");
  const [loading, setLoading] = useState(false);
  const [supportMessage, setSupportMessage] = useState(
    summary.support_message || ""
  );

  useEffect(() => {
    if (
      (proposal.status === "cancelled" || proposal.status === "rejected") &&
      status === "pending"
    ) {
      setStatus("cancelled");
    }
  }, [proposal.status, status]);

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const payload = {
        store_id: homeStore,
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action,
      };
      if (
        action === "confirm" &&
        supportMessage.trim() !== (summary.support_message || "").trim()
      ) {
        payload.overrides = { support_message: supportMessage.trim() };
      }
      const res = await postRightworkAPI(API_ops_assistant_confirm_action, payload);
      if (res.status === 200) {
        const newStatus = action === "confirm" ? "confirmed" : "cancelled";
        setStatus(newStatus);
        onStatusChange?.(proposal.proposal_id, newStatus);
      } else {
        toast.error("Failed to process action.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message ||
          err?.response?.data?.error ||
          "Failed to process action."
      );
    }
    setLoading(false);
  };

  if (status === "confirmed") {
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 my-2">
        <CheckCircleIcon fontSize="small" className="text-green-600 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-medium">Support request sent</div>
          {summary.original_question && (
            <div className="text-xs opacity-80 truncate">{summary.original_question}</div>
          )}
        </div>
      </div>
    );
  }

  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Support request cancelled</span>
      </div>
    );
  }

  const overLimit = supportMessage.length > MAX_LEN;

  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <SupportAgentIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">
          {summary.title || "Send to Rightwork Support"}
        </span>
      </div>

      <div className="px-3 py-2 space-y-3">
        {summary.original_question && (
          <div className="text-sm">
            <div className="text-gray-500 mb-1">Original question</div>
            <div className="rounded-md bg-white border border-gray-200 px-3 py-2 text-gray-700">
              {summary.original_question}
            </div>
          </div>
        )}

        <div>
          <div className="text-sm text-gray-500 mb-1">Support message</div>
          <textarea
            value={supportMessage}
            onChange={(e) => setSupportMessage(e.target.value)}
            maxLength={MAX_LEN}
            className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 resize-none bg-white"
            rows={Math.min(Math.max(supportMessage.split("\n").length, 5), 8)}
            placeholder="Describe what Rovis could not answer..."
          />
          <div className={`text-xs text-right mt-0.5 ${overLimit ? "text-red-500" : "text-gray-400"}`}>
            {supportMessage.length} / {MAX_LEN}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
        <button
          onClick={() => handleAction("reject")}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => handleAction("confirm")}
          disabled={loading || !supportMessage.trim() || overLimit}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loading && <CircularProgress size={14} sx={{ color: "white" }} />}
          Send
        </button>
      </div>
    </div>
  );
}
